import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";

/**
 * Blocks background page scroll while an overlay (typically an inline dialog) is
 * open. Ref-counted so nested/stacked dialogs each add a lock and scrolling is
 * only restored once the last one releases it.
 *
 * CDK dialogs (`@angular/cdk/dialog`) and NgbModal already block scroll on their
 * own, so this is meant for the app's inline `fixed inset-0` dialogs, which do
 * not go through those overlay systems.
 *
 * Locking pins `body` with `position: fixed` (compensating with a negative
 * `top`) instead of just toggling `overflow: hidden`. WebKit (Safari/iOS,
 * i.e. the Capacitor WebView) resets `scrollTop` to 0 as soon as `overflow:
 * hidden` is applied to `body`, which would otherwise jump the page to the
 * top the moment a dialog opens.
 */
@Injectable({ providedIn: "root" })
export class ScrollLockService {
  private static readonly LOCK_CLASS = "scroll-locked";

  private lockCount = 0;
  private scrollY = 0;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  lock(): void {
    this.lockCount++;
    if (this.lockCount === 1) {
      this.scrollY = window.scrollY;
      this.document.documentElement.classList.add(ScrollLockService.LOCK_CLASS);
      this.document.body.style.top = `-${this.scrollY}px`;
    }
  }

  unlock(): void {
    if (this.lockCount === 0) {
      return;
    }
    this.lockCount--;
    if (this.lockCount === 0) {
      this.document.documentElement.classList.remove(
        ScrollLockService.LOCK_CLASS
      );
      this.document.body.style.top = "";
      window.scrollTo(0, this.scrollY);
    }
  }
}
